"use client";

import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { deleteWithAuth } from "@/lib/api-helpers";
import { cn } from "@/lib/utils";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { integrationKeys, useMcpIntegrations } from "@/hooks/integrations";
import type { McpIntegrationPublic } from "@/hooks/integrations";
import { BrandIcon } from "@/components/tailwind/ui/BrandIcon";
import McpConnectModal from "../modals/McpConnectModal";

/**
 * Settings → Closot MCP — the Notion-style "Notion MCP" page.
 *
 * Discover: MCP clients that can connect to Closot's MCP server (Claude only
 * for now). Clicking a client opens the authorize → one-time-token flow in
 * McpConnectModal.
 *
 * Manage: the caller's active integration tokens. Rotation runs inside
 * McpConnectModal (confirm → new token reveal); revoke stays here behind a
 * window.confirm — its response carries no secret, so an interrupted request
 * loses nothing.
 *
 * workspaceId comes exclusively from the workspace context — NOT from the
 * localStorage fallback other tabs use. That cache is shared across browser
 * tabs, and minting a credential against a workspace other than the one on
 * screen would make the consent screen lie. Until the context hydrates we
 * show the loading skeleton and keep Connect disabled.
 */

/** MCP clients shown under Discover. Only Claude is wired up for now. */
const MCP_CLIENTS = [
  {
    id: "claude",
    name: "Claude",
  },
] as const;

type ModalState =
  | { mode: "connect" }
  | { mode: "rotate"; integration: McpIntegrationPublic }
  | null;

function formatDate(value: string | null): string {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Never";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function ClosotMcpContent() {
  const { currentWorkspace } = useWorkspaceContext();
  const queryClient = useQueryClient();

  const workspaceId = currentWorkspace?._id ?? "";

  const [activeTab, setActiveTab] = useState<"discover" | "manage">("discover");
  const [modal, setModal] = useState<ModalState>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const { activeIntegrations, isConnected, isLoading, isError, refetch } =
    useMcpIntegrations(workspaceId);

  // Context not hydrated yet → indistinguishable from loading, render it as such.
  const showSkeleton = isLoading || !workspaceId;

  const handleRevoke = async (integration: McpIntegrationPublic) => {
    if (revokingId) return;
    if (
      !window.confirm(
        `Revoke "${integration.name}"?\n\nThe token stops working immediately and cannot be restored. Claude will lose access to this workspace until you connect again.`,
      )
    )
      return;

    setRevokingId(integration.id);
    try {
      const res = await deleteWithAuth(`/api/mcp-integrations/${integration.id}`);
      if (res && typeof res === "object" && "isError" in res && (res as { isError?: boolean }).isError) {
        alert(`Failed to revoke: ${(res as { message?: string }).message ?? "Unknown error"}`);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: integrationKeys.mcp(workspaceId) });
    } catch {
      // deleteWithAuth throws only on 401, after already redirecting to
      // sign-in — nothing to surface here.
    } finally {
      setRevokingId(null);
    }
  };

  const renderError = () => (
    <div className="flex flex-col items-start gap-2 rounded-xl border border-zinc-200 dark:border-zinc-800 px-4 py-5">
      <div className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
        Couldn&apos;t load your connections. This doesn&apos;t affect existing tokens.
      </div>
      <button
        type="button"
        onClick={() => void refetch()}
        className="text-sm font-medium text-blue-500 hover:underline"
      >
        Try again
      </button>
    </div>
  );

  const renderSkeletonRows = (count: number) => (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="h-16 rounded-xl border border-zinc-200 dark:border-zinc-800 animate-pulse bg-[rgb(225,225,224)] dark:bg-[rgb(45,45,45)]"
        />
      ))}
    </div>
  );

  const renderDiscover = () => (
    <div>
      <div className="mb-1 text-base font-medium text-zinc-900 dark:text-zinc-100">
        Discover MCP clients
      </div>
      <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
        These MCP clients can be connected to Closot&apos;s MCP server
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {MCP_CLIENTS.map((client) => (
          <div
            key={client.id}
            role="button"
            tabIndex={0}
            aria-disabled={!workspaceId}
            onClick={() => {
              if (workspaceId) setModal({ mode: "connect" });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (workspaceId) setModal({ mode: "connect" });
              }
            }}
            className="user-select-none transition-colors duration-200 ease-in cursor-pointer flex items-center gap-3 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800 hover-component"
          >
            <BrandIcon id={client.id} size={36} alt={client.name} className="flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {client.name}
              </div>
              {client.id === "claude" && isConnected && (
                <div className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                  Connected
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderManage = () => (
    <div>
      <div className="mb-1 text-base font-medium text-zinc-900 dark:text-zinc-100">
        Active connections
      </div>
      <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
        Tokens you created for this workspace. Each acts with your permissions.
      </p>

      {showSkeleton ? (
        renderSkeletonRows(2)
      ) : isError ? (
        renderError()
      ) : activeIntegrations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 dark:border-zinc-800 px-4 py-8 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No connections yet.</p>
          <button
            type="button"
            onClick={() => setActiveTab("discover")}
            className="mt-2 text-sm font-medium text-blue-500 hover:underline"
          >
            Connect a client from Discover
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {activeIntegrations.map((integration) => {
            const isRevoking = revokingId === integration.id;
            // A revoke in flight disables actions on EVERY row — handleRevoke
            // is single-flight, so a sibling button that looked enabled would
            // silently no-op.
            const actionsDisabled = revokingId !== null;
            return (
              <div
                key={integration.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800"
              >
                <BrandIcon id="claude" size={32} className="flex-shrink-0 hidden sm:block" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                    {integration.name}
                  </div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 font-mono truncate">
                    {integration.tokenPrefix}…
                  </div>
                  <div className="mt-0.5 text-xs text-zinc-400 dark:text-zinc-500">
                    Created {formatDate(integration.createdAt)} · Last used{" "}
                    {formatDate(integration.lastUsedAt)}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    disabled={actionsDisabled}
                    onClick={() => setModal({ mode: "rotate", integration })}
                    className="h-7 px-2.5 rounded-md text-xs font-medium text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-50 transition-colors"
                  >
                    Rotate token
                  </button>
                  <button
                    type="button"
                    disabled={actionsDisabled}
                    onClick={() => void handleRevoke(integration)}
                    className="h-7 px-2.5 rounded-md text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 disabled:opacity-50 transition-colors"
                  >
                    {isRevoking ? "Revoking…" : "Revoke"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full w-auto">
      <div className="flex-grow transform translate-z-0 overflow-auto clean-scrollbar">
        {/* Page header */}
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">Closot MCP</h1>
        <p className="mt-1 mb-5 text-sm text-zinc-500 dark:text-zinc-400">
          Let your favorite apps and tools take actions in Closot via our MCP server.{" "}
          <a
            href="https://www.npmjs.com/package/@closot/mcp"
            target="_blank"
            rel="noreferrer"
            className="text-blue-500 hover:underline"
          >
            Learn more
          </a>
        </p>

        {/* Discover / Manage pills */}
        <div className="flex items-center gap-1 mb-6">
          {(
            [
              { id: "discover", label: "Discover" },
              { id: "manage", label: "Manage" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              aria-pressed={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "h-8 px-3.5 rounded-full text-sm font-medium transition-colors",
                activeTab === tab.id
                  ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
                  : "text-zinc-500 dark:text-zinc-400 hover-component",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "discover" ? renderDiscover() : renderManage()}
      </div>

      {/* Connect / rotate flow — mounted fresh per open so no stale state can
          flash, and the modal itself guards the one-time token reveal. */}
      {modal && (
        <McpConnectModal
          // The key forces a full remount if the modal state ever swaps
          // without passing through null (e.g. keyboard reaches a background
          // button) — fresh state per flow is a hard invariant here.
          key={modal.mode === "rotate" ? `rotate-${modal.integration.id}` : "connect"}
          onClose={() => setModal(null)}
          workspaceId={workspaceId}
          workspaceName={currentWorkspace?.name}
          rotateIntegration={
            modal.mode === "rotate"
              ? { id: modal.integration.id, name: modal.integration.name }
              : null
          }
        />
      )}
    </div>
  );
}
